import { CircleAlert, CircleCheck, RefreshCw, Server } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Skeleton } from '../../../components/ui/Skeleton';
import { ApiError } from '../../../services/http';
import { useApiMeta } from '../queries';

function formatSkew(serverTime: string, fetchedAt: number): string {
  const skewMs = Date.parse(serverTime) - fetchedAt;
  const seconds = Math.round(Math.abs(skewMs) / 1000);
  return seconds < 2 ? 'in sync' : `${seconds}s ${skewMs > 0 ? 'ahead' : 'behind'}`;
}

/** Phase 1 status page: proves the web → API path end to end (AC-1.9). */
export function SystemStatusPage() {
  const meta = useApiMeta();

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-fg">System status</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Connectivity between this web app and the Lead Management API.
        </p>
      </header>

      <Card>
        <CardHeader
          title="API"
          description="GET /api/v1/meta"
          actions={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void meta.refetch()}
              loading={meta.isFetching}
              leadingIcon={<RefreshCw aria-hidden className="size-4" />}
            >
              Refresh
            </Button>
          }
        />
        <CardBody>
          {meta.isPending ? (
            <div className="space-y-3" aria-label="Loading API status">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-56" />
            </div>
          ) : meta.isError ? (
            <EmptyState
              icon={<CircleAlert aria-hidden className="size-6" />}
              title="API unreachable"
              description={
                meta.error instanceof ApiError && !meta.error.isNetworkError
                  ? `${meta.error.message} (${meta.error.code})`
                  : 'The server could not be reached. Make sure it is running on port 4000.'
              }
              action={
                <Button variant="secondary" onClick={() => void meta.refetch()}>
                  Try again
                </Button>
              }
            />
          ) : (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Status</dt>
                <dd className="mt-1">
                  <Badge tone="success">
                    <CircleCheck aria-hidden className="size-3.5" /> Connected
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Platform</dt>
                <dd className="mt-1 flex items-center gap-2 text-sm text-fg">
                  <Server aria-hidden className="size-4 text-fg-muted" />
                  {meta.data.platformName}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-fg-subtle">API version</dt>
                <dd className="mt-1 text-sm text-fg">{meta.data.apiVersion}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-fg-subtle">Server clock</dt>
                <dd className="mt-1 text-sm text-fg tabular-nums">
                  {new Date(meta.data.serverTime).toLocaleString()} ·{' '}
                  {formatSkew(meta.data.serverTime, meta.dataUpdatedAt)}
                </dd>
              </div>
            </dl>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
